import { getBrandSeriesColor } from "@/config/visualizationTokens";
import { getChartDefaultOptions } from "./ChartJsConfig";
import { useTheme } from "@mui/material/styles";
import appDefaultTheme from "@/config/muiTheme";
import { getChartTextColor } from "./chartTextColor";
import React, { useState, useEffect, useMemo } from "react";
import * as ReactChartJs from "react-chartjs-2";
import moment from "moment";
import { RPNumberFormat } from "@/helpers/utils";

type MonthlyBrandSpend = {
  month_year?: string;
  monthly_total_spend_brand?: string | number | null;
};

type BrandSpendSeries = {
  brand_name?: string;
  data?: MonthlyBrandSpend[];
};

type DataFormat = {
  prefix?: string;
  suffix: string;
};

type DataFormats = {
  monthly_total_spend_brand?: DataFormat;
};

type MultiBrandTotalSpendChartProps = {
  data?: BrandSpendSeries[];
  option?: Record<string, unknown>;
  scales?: Record<string, unknown>;
  formats?: DataFormats | null;
};

type TooltipItem = {
  datasetIndex: number;
  value?: string | number | null;
};

type TooltipData = {
  datasets: Array<{
    label: string;
  }>;
};

type ChartDataState = {
  labels: string[];
  datasets: Array<Record<string, unknown>>;
};

const { Bar } = ReactChartJs as unknown as {
  Bar: React.ComponentType<Record<string, unknown>>;
};
const MultiBrandTotalSpendChart = ({
  data = [],
  option = {},
  scales = {},
  formats = null,
}: MultiBrandTotalSpendChartProps) => {
  const providedTheme = useTheme();
  const theme = providedTheme.palette.neutral ? providedTheme : appDefaultTheme;
  const [chartData, setChartData] = useState<ChartDataState>({
    labels: [],
    datasets: [],
  });

  const options = useMemo(
    () => ({
      ...getChartDefaultOptions(theme),
      responsive: true,
      maintainAspectRatio: false,
      tooltips: {
        ...getChartDefaultOptions(theme).tooltips,
        callbacks: {
          title: (tooltipItem: Array<{ label: string }>) => {
            const label = tooltipItem[0].label;
            return moment(label, "YYYY-MM").format("MMM YYYY");
          },
          label: (tooltipItem: TooltipItem, data: TooltipData) => {
            const { datasetIndex, value } = tooltipItem;
            const label = data.datasets[datasetIndex].label;
            const format = formats ? formats.monthly_total_spend_brand : null;

            if (!value) return "";
            if (formats && format) {
              return `${label}: ${RPNumberFormat({
                prefix: format.prefix || "$",
                suffix: format.suffix,
                thousand: ",",
              }).to(parseFloat(`${value}`))}`;
            }
            return `${label}: ${RPNumberFormat({
              prefix: "$",
              suffix: "",
              thousand: ",",
            }).to(parseFloat(`${value}`))}`;
          },
        },
      },
      legend: {
        display: true,
        position: "bottom",
        align: "center",
        labels: {
          boxWidth: 12,
          fontColor: getChartTextColor(theme),
          fontFamily: theme.typography.fontFamily,
          padding: 15,
          usePointStyle: true,
        },
        onClick: () => {},
      },
      plugins: {
        datalabels: {
          display: false,
        },
      },
      scales: {
        fontFamily: theme.typography.fontFamily,
        xAxes: [
          {
            stacked: true,
            gridLines: {
              display: false,
            },
            ticks: {
              fontColor: getChartTextColor(theme),
              callback: (value: string) => {
                return moment(value, "YYYY-MM").format("MMM");
              },
            },
          },
        ],
        yAxes: [
          {
            id: "y-axis-0",
            position: "left",
            stacked: true,
            ticks: {
              fontColor: getChartTextColor(theme),
              min: 0,
              maxTicksLimit: 5,
              callback: (value: string | number) => {
                const format = formats
                  ? formats.monthly_total_spend_brand
                  : null;
                if (!value) return "";
                if (formats && format) {
                  return RPNumberFormat({
                    prefix: format.prefix,
                    suffix: format.suffix,
                    thousand: ",",
                  }).to(value);
                }
                return RPNumberFormat().to(value);
              },
            },
          },
        ],
        ...scales,
      },
      ...option,
    }),
    [option, scales, formats, theme]
  );

  const getDataSet = () => {
    if (!data || data.length === 0) {
      return {
        labels: [],
        datasets: [],
      };
    }

    // Get all unique months from all brands
    const allMonths = new Set<string>();
    data.forEach((brandData) => {
      if (brandData.data && Array.isArray(brandData.data)) {
        brandData.data.forEach((item) => {
          if (item.month_year) {
            allMonths.add(item.month_year);
          }
        });
      }
    });

    // Sort months chronologically
    const sortedMonths = Array.from(allMonths).sort();

    // Create datasets for each brand
    const datasets = data
      .filter(
        (brandData) =>
          brandData.data &&
          Array.isArray(brandData.data) &&
          brandData.data.length > 0
      )
      .map((brandData, index) => {
        const brandName = brandData.brand_name || `Brand ${index + 1}`;
        const brandColor = getBrandSeriesColor(index); // Use set 3 colors, cycle through 7 colors

        // Create data array for this brand
        const brandDataArray = sortedMonths.map((month) => {
          const monthData = brandData.data?.find(
            (item) => item.month_year === month
          );
          return monthData ? monthData.monthly_total_spend_brand : 0;
        });

        return {
          label: brandName,
          data: brandDataArray,
          yAxisID: "y-axis-0",
          backgroundColor: brandColor,
          hoverBackgroundColor: brandColor,
          borderColor: brandColor,
          borderWidth: 0,
        };
      });

    return {
      labels: sortedMonths,
      datasets: datasets,
    };
  };

  useEffect(() => {
    setChartData(getDataSet());
  }, [data, theme]);

  return (
    <div className="bar-chart multi-brand-total-spend-bar-chart">
      <div className="row">
        <div className="col-12">
          <div style={{ height: "400px", minHeight: "300px" }}>
            <Bar data={chartData} options={options} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiBrandTotalSpendChart;
